<script setup lang="ts">
import { ref, inject, onMounted } from 'vue'
import type { Ref } from 'vue'
import axios from 'axios'
import { toast } from 'vue-sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { KeyRound, Plus, Trash2, Eye, EyeOff, Loader2, RefreshCw, Code2, Shuffle, ExternalLink } from 'lucide-vue-next'

const vhostId = inject<Ref<string>>('vhostId', ref('0'))

// ─── Types ───────────────────────────────────────────────────────────────────

interface AICredRow {
  id: number
  cred_label: string
  clientid: string
  has_secret: boolean
  secret_masked: string
  model: string
  spend_cap_cents: number | null
  // UI-only
  editSecret: boolean
  newSecret: string
  showSecret: boolean
  saving: boolean
  spendCapDollars: string
}

interface AIProviderGroup {
  provider: string
  provider_label: string
  models: string[]
  credentials: AICredRow[]
  // UI-only
  showAddForm: boolean
  newCred: NewCredForm
}

interface NewCredForm {
  cred_label: string
  clientid: string
  secret: string
  model: string
  spendCapDollars: string
  showSecret: boolean
  saving: boolean
}

interface IntegrationCredential {
  id: number
  type: string
  clientid: string
  has_secret: boolean
}

interface ClientTransfer {
  id: number
  name: string
  protocol: string
}

// ─── State ───────────────────────────────────────────────────────────────────

const loading = ref(false)
const aiGroups = ref<AIProviderGroup[]>([])
const integrations = ref<IntegrationCredential[]>([])
const clientTransfers = ref<ClientTransfer[]>([])

// Integration CRUD
const showAddIntegration = ref(false)
const newIntegrationType = ref('')
const newIntegrationClientId = ref('')
const newIntegrationSecret = ref('')
const savingIntegration = ref(false)
const editingIntegration = ref<IntegrationCredential | null>(null)
const editIntClientId = ref('')
const editIntSecret = ref('')

const activeSection = ref<'ai' | 'integrations' | 'transfers' | 'apikeys'>('ai')

// ─── Helpers ──────────────────────────────────────────────────────────────────

function newCredTemplate(): NewCredForm {
  return { cred_label: '', clientid: '', secret: '', model: '', spendCapDollars: '', showSecret: false, saving: false }
}

function hydrateCredRow(c: any): AICredRow {
  return {
    id: c.id,
    cred_label: c.cred_label || '',
    clientid: c.clientid || '',
    has_secret: c.has_secret,
    secret_masked: c.secret_masked || '',
    model: c.model || '',
    spend_cap_cents: c.spend_cap_cents ?? null,
    editSecret: false,
    newSecret: '',
    showSecret: false,
    saving: false,
    spendCapDollars: c.spend_cap_cents ? String(Math.round(c.spend_cap_cents / 100)) : '',
  }
}

// ─── Load ─────────────────────────────────────────────────────────────────────

const loadCredentials = async () => {
  loading.value = true
  try {
    const response = await axios.post('/services/json', {
      method: 'vhost.getCredentials',
      vhostId: Number(vhostId.value),
    })
    if (response.data?.status) {
      const r = response.data.result
      aiGroups.value = (r.ai || []).map((group: any): AIProviderGroup => ({
        provider: group.provider,
        provider_label: group.provider_label,
        models: group.models || [],
        credentials: (group.credentials || []).map(hydrateCredRow),
        showAddForm: false,
        newCred: newCredTemplate(),
      }))
      integrations.value = r.integrations || []
      clientTransfers.value = r.client_transfers || []
    }
  } catch {
    toast.error('Failed to load credentials')
  } finally {
    loading.value = false
  }
}

// ─── AI Credentials ──────────────────────────────────────────────────────────

const saveAICredential = async (cred: AICredRow, provider: string) => {
  cred.saving = true
  try {
    const response = await axios.post('/services/json', {
      method: 'vhost.saveAICredential',
      vhostId: Number(vhostId.value),
      provider,
      credLabel: cred.cred_label,
      clientid: cred.clientid || '',
      secret: cred.newSecret || '',
      model: cred.model || '',
      spend_cap_cents: cred.spendCapDollars ? Math.round(parseFloat(cred.spendCapDollars) * 100) : null,
      id: cred.id,
    })
    if (response.data?.status) {
      const updated = response.data.result
      cred.has_secret = updated.has_secret
      cred.secret_masked = updated.secret_masked
      cred.editSecret = false
      cred.newSecret = ''
      toast.success(`"${updated.cred_label || 'Key'}" saved`)
    } else {
      toast.error(response.data?.result || 'Save failed')
    }
  } catch {
    toast.error('Save failed')
  } finally {
    cred.saving = false
  }
}

const createAICredential = async (group: AIProviderGroup) => {
  const nc = group.newCred
  if (!nc.cred_label || !nc.secret) return
  nc.saving = true
  try {
    const response = await axios.post('/services/json', {
      method: 'vhost.saveAICredential',
      vhostId: Number(vhostId.value),
      provider: group.provider,
      credLabel: nc.cred_label,
      clientid: nc.clientid || '',
      secret: nc.secret,
      model: nc.model || '',
      spend_cap_cents: nc.spendCapDollars ? Math.round(parseFloat(nc.spendCapDollars) * 100) : null,
      id: null,
    })
    if (response.data?.status) {
      group.credentials.push(hydrateCredRow(response.data.result))
      group.showAddForm = false
      group.newCred = newCredTemplate()
      toast.success(`"${response.data.result.cred_label}" added`)
    } else {
      toast.error(response.data?.result || 'Save failed')
    }
  } catch {
    toast.error('Save failed')
  } finally {
    nc.saving = false
  }
}

const deleteAICredential = async (cred: AICredRow, group: AIProviderGroup) => {
  const label = cred.cred_label || group.provider_label
  if (!confirm(`Remove "${label}"? The project will fall back to the platform key.`)) return
  try {
    const response = await axios.post('/services/json', {
      method: 'vhost.deleteAICredential',
      vhostId: Number(vhostId.value),
      credentialId: cred.id,
    })
    if (response.data?.status) {
      group.credentials = group.credentials.filter(c => c.id !== cred.id)
      toast.success('Credential removed')
    } else {
      toast.error(response.data?.result || 'Delete failed')
    }
  } catch {
    toast.error('Delete failed')
  }
}

// ─── Integration Credentials ─────────────────────────────────────────────────

const saveIntegrationCredential = async () => {
  if (!editingIntegration.value) return
  savingIntegration.value = true
  try {
    const response = await axios.post('/services/json', {
      method: 'vhost.saveExternalCredential',
      vhostId: Number(vhostId.value),
      id: editingIntegration.value.id,
      clientid: editIntClientId.value,
      secret: editIntSecret.value,
    })
    if (response.data?.status) {
      toast.success('Integration credential updated')
      editingIntegration.value = null
      await loadCredentials()
    } else {
      toast.error(response.data?.message || 'Save failed')
    }
  } catch {
    toast.error('Save failed')
  } finally {
    savingIntegration.value = false
  }
}

const addIntegrationCredential = async () => {
  if (!newIntegrationType.value || !newIntegrationClientId.value) return
  savingIntegration.value = true
  try {
    const response = await axios.post('/services/json', {
      method: 'vhost.saveExternalCredential',
      vhostId: Number(vhostId.value),
      type: newIntegrationType.value,
      clientid: newIntegrationClientId.value,
      secret: newIntegrationSecret.value,
    })
    if (response.data?.status) {
      toast.success('Integration added')
      showAddIntegration.value = false
      newIntegrationType.value = ''
      newIntegrationClientId.value = ''
      newIntegrationSecret.value = ''
      await loadCredentials()
    } else {
      toast.error(response.data?.message || 'Save failed')
    }
  } catch {
    toast.error('Save failed')
  } finally {
    savingIntegration.value = false
  }
}

const deleteIntegrationCredential = async (id: number) => {
  if (!confirm('Remove this integration credential?')) return
  try {
    const response = await axios.post('/services/json', {
      method: 'vhost.deleteExternalCredential',
      vhostId: Number(vhostId.value),
      id,
    })
    if (response.data?.status) {
      toast.success('Credential removed')
      await loadCredentials()
    }
  } catch {
    toast.error('Delete failed')
  }
}

const startEditIntegration = (cred: IntegrationCredential) => {
  editingIntegration.value = cred
  editIntClientId.value = cred.clientid || ''
  editIntSecret.value = ''
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(async () => {
  await loadCredentials()
})
</script>

<template>
  <div class="flex flex-col lg:flex-row min-h-[80vh]">
    <!-- Sidebar nav -->
    <aside class="w-full lg:w-56 shrink-0 border-r bg-muted/30 p-4 space-y-1">
      <p class="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3 px-2">Credentials</p>
      <button
        v-for="item in [
          { key: 'ai',           label: 'AI Providers',      icon: KeyRound },
          { key: 'integrations', label: 'Integrations',       icon: Shuffle },
          { key: 'transfers',    label: 'Client Transfers',   icon: RefreshCw },
          { key: 'apikeys',      label: 'Developer API Keys', icon: Code2 },
        ]"
        :key="item.key"
        class="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors text-left"
        :class="activeSection === item.key
          ? 'bg-primary text-primary-foreground font-medium'
          : 'hover:bg-muted text-foreground'"
        @click="activeSection = (item.key as any)"
      >
        <component :is="item.icon" class="h-4 w-4 shrink-0" />
        {{ item.label }}
      </button>
    </aside>

    <!-- Main content -->
    <main class="flex-1 p-4 lg:p-8 space-y-6 min-w-0">
      <div v-if="loading" class="flex items-center gap-2 text-muted-foreground text-sm">
        <Loader2 class="h-4 w-4 animate-spin" /> Loading credentials…
      </div>

      <!-- ═══ AI Providers ═══ -->
      <template v-if="activeSection === 'ai'">
        <div class="mb-2">
          <h1 class="text-lg font-semibold">AI Provider Keys</h1>
          <p class="text-sm text-muted-foreground mt-0.5">Each provider can have multiple named credentials. Select which to use per feature.</p>
        </div>
        <Separator />

        <div class="space-y-8 mt-4">
          <!-- Provider group -->
          <div v-for="group in aiGroups" :key="group.provider">
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-2">
                <KeyRound class="h-4 w-4 text-muted-foreground" />
                <span class="font-medium text-sm">{{ group.provider_label }}</span>
                <code class="text-xs text-muted-foreground">{{ group.provider }}</code>
                <Badge v-if="group.credentials.length" variant="secondary" class="text-xs">
                  {{ group.credentials.length }} configured
                </Badge>
                <Badge v-else variant="outline" class="text-xs text-muted-foreground">Not set</Badge>
              </div>
              <Button size="sm" variant="outline" class="h-7 text-xs gap-1" @click="group.showAddForm = true; group.newCred = newCredTemplate()">
                <Plus class="h-3.5 w-3.5" /> Add Key
              </Button>
            </div>

            <!-- Existing credentials -->
            <div class="space-y-2">
              <div
                v-for="cred in group.credentials"
                :key="cred.id"
                class="rounded-lg border p-4 space-y-3"
              >
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div class="grid gap-1">
                    <Label class="text-xs">Name / Label</Label>
                    <Input v-model="cred.cred_label" placeholder="e.g. Production" class="text-sm h-8" />
                  </div>
                  <div class="grid gap-1">
                    <Label class="text-xs">Client / Org ID <span class="text-muted-foreground font-normal">(optional)</span></Label>
                    <Input v-model="cred.clientid" placeholder="e.g. org-abc123" class="text-sm h-8" />
                  </div>
                  <div class="grid gap-1">
                    <Label class="text-xs">API Key</Label>
                    <div v-if="cred.has_secret && !cred.editSecret" class="flex items-center gap-2 h-8">
                      <code class="text-xs text-muted-foreground">{{ cred.secret_masked }}</code>
                      <Button size="sm" variant="ghost" class="h-6 text-xs px-2" @click="cred.editSecret = true; cred.newSecret = ''">Replace</Button>
                    </div>
                    <div v-else class="relative">
                      <Input
                        v-model="cred.newSecret"
                        :type="cred.showSecret ? 'text' : 'password'"
                        placeholder="Paste API key…"
                        class="text-sm h-8 pr-9 font-mono"
                        autocomplete="off"
                      />
                      <button
                        type="button"
                        class="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        @click="cred.showSecret = !cred.showSecret"
                      >
                        <Eye v-if="!cred.showSecret" class="h-3.5 w-3.5" />
                        <EyeOff v-else class="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div class="grid gap-1">
                    <Label class="text-xs">Default Model <span class="text-muted-foreground font-normal">(optional)</span></Label>
                    <select
                      v-if="group.models.length"
                      v-model="cred.model"
                      class="h-8 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="">Provider default</option>
                      <option v-for="m in group.models" :key="m" :value="m">{{ m }}</option>
                    </select>
                    <Input v-else v-model="cred.model" placeholder="e.g. gpt-4o" class="text-sm h-8" />
                  </div>
                  <div class="grid gap-1">
                    <Label class="text-xs">Monthly Spend Cap USD <span class="text-muted-foreground font-normal">(optional)</span></Label>
                    <div class="relative">
                      <span class="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                      <Input v-model="cred.spendCapDollars" type="number" min="0" step="1" placeholder="No limit" class="text-sm h-8 pl-5" />
                    </div>
                  </div>
                </div>

                <div class="flex items-center justify-between pt-1">
                  <Button size="sm" variant="ghost" class="h-7 text-xs text-destructive hover:text-destructive gap-1" @click="deleteAICredential(cred, group)">
                    <Trash2 class="h-3.5 w-3.5" /> Delete
                  </Button>
                  <div class="flex gap-2">
                    <Button v-if="cred.editSecret" size="sm" variant="ghost" class="h-7 text-xs" @click="cred.editSecret = false; cred.newSecret = ''">Cancel</Button>
                    <Button size="sm" class="h-7 text-xs gap-1" :disabled="cred.saving" @click="saveAICredential(cred, group.provider)">
                      <Loader2 v-if="cred.saving" class="h-3 w-3 animate-spin" />
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Add new credential form -->
            <div v-if="group.showAddForm" class="rounded-lg border border-primary p-4 space-y-3 mt-2">
              <p class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">New {{ group.provider_label }} Key</p>
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div class="grid gap-1">
                  <Label class="text-xs">Name / Label <span class="text-destructive">*</span></Label>
                  <Input v-model="group.newCred.cred_label" placeholder="e.g. Production" class="text-sm h-8" />
                </div>
                <div class="grid gap-1">
                  <Label class="text-xs">Client / Org ID <span class="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input v-model="group.newCred.clientid" placeholder="e.g. org-abc123" class="text-sm h-8" />
                </div>
                <div class="grid gap-1">
                  <Label class="text-xs">API Key <span class="text-destructive">*</span></Label>
                  <div class="relative">
                    <Input
                      v-model="group.newCred.secret"
                      :type="group.newCred.showSecret ? 'text' : 'password'"
                      placeholder="Paste API key…"
                      class="text-sm h-8 pr-9 font-mono"
                      autocomplete="off"
                    />
                    <button
                      type="button"
                      class="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      @click="group.newCred.showSecret = !group.newCred.showSecret"
                    >
                      <Eye v-if="!group.newCred.showSecret" class="h-3.5 w-3.5" />
                      <EyeOff v-else class="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div class="grid gap-1">
                  <Label class="text-xs">Default Model <span class="text-muted-foreground font-normal">(optional)</span></Label>
                  <select
                    v-if="group.models.length"
                    v-model="group.newCred.model"
                    class="h-8 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="">Provider default</option>
                    <option v-for="m in group.models" :key="m" :value="m">{{ m }}</option>
                  </select>
                  <Input v-else v-model="group.newCred.model" placeholder="e.g. gpt-4o" class="text-sm h-8" />
                </div>
                <div class="grid gap-1">
                  <Label class="text-xs">Monthly Spend Cap USD <span class="text-muted-foreground font-normal">(optional)</span></Label>
                  <div class="relative">
                    <span class="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
                    <Input v-model="group.newCred.spendCapDollars" type="number" min="0" step="1" placeholder="No limit" class="text-sm h-8 pl-5" />
                  </div>
                </div>
              </div>
              <div class="flex justify-end gap-2">
                <Button size="sm" variant="ghost" class="h-7 text-xs" @click="group.showAddForm = false">Cancel</Button>
                <Button
                  size="sm"
                  class="h-7 text-xs gap-1"
                  :disabled="group.newCred.saving || !group.newCred.cred_label || !group.newCred.secret"
                  @click="createAICredential(group)"
                >
                  <Loader2 v-if="group.newCred.saving" class="h-3 w-3 animate-spin" />
                  Add Key
                </Button>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- ═══ Integrations ═══ -->
      <template v-if="activeSection === 'integrations'">
        <div class="flex items-center justify-between mb-2">
          <div>
            <h1 class="text-lg font-semibold">Integration Credentials</h1>
            <p class="text-sm text-muted-foreground mt-0.5">Credentials for external services like Frame.io, TMS platforms, and others.</p>
          </div>
          <Button size="sm" class="gap-1.5" @click="showAddIntegration = true">
            <Plus class="h-4 w-4" /> Add
          </Button>
        </div>
        <Separator />

        <div class="mt-4 space-y-3">
          <div
            v-for="cred in integrations"
            :key="cred.id"
            class="rounded-lg border p-4"
          >
            <div class="flex items-center justify-between">
              <div>
                <div class="text-sm font-medium">{{ cred.type }}</div>
                <div class="text-xs text-muted-foreground mt-0.5">{{ cred.clientid || 'No client ID' }}</div>
              </div>
              <div class="flex gap-2">
                <Button size="sm" variant="outline" class="h-7 text-xs" @click="startEditIntegration(cred)">Edit</Button>
                <Button size="sm" variant="ghost" class="h-7 text-xs text-destructive hover:text-destructive" @click="deleteIntegrationCredential(cred.id)">
                  <Trash2 class="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            <div v-if="editingIntegration?.id === cred.id" class="mt-3 space-y-3 border-t pt-3">
              <div class="grid gap-1.5">
                <Label class="text-xs">Client ID</Label>
                <Input v-model="editIntClientId" class="text-sm h-8" />
              </div>
              <div class="grid gap-1.5">
                <Label class="text-xs">Secret</Label>
                <Input v-model="editIntSecret" type="password" placeholder="Leave blank to keep existing" class="text-sm h-8" autocomplete="off" />
              </div>
              <div class="flex gap-2">
                <Button size="sm" class="h-7 text-xs gap-1" :disabled="savingIntegration" @click="saveIntegrationCredential">
                  <Loader2 v-if="savingIntegration" class="h-3 w-3 animate-spin" />
                  Save
                </Button>
                <Button size="sm" variant="ghost" class="h-7 text-xs" @click="editingIntegration = null">Cancel</Button>
              </div>
            </div>
          </div>

          <p v-if="!integrations.length" class="text-sm text-muted-foreground py-4">No integration credentials configured.</p>
        </div>
      </template>

      <!-- ═══ Client Transfers ═══ -->
      <template v-if="activeSection === 'transfers'">
        <div class="mb-2">
          <h1 class="text-lg font-semibold">Client Transfers</h1>
          <p class="text-sm text-muted-foreground mt-0.5">Read-only summary. Manage transfer profiles in Settings → Transfers.</p>
        </div>
        <Separator />

        <div class="mt-4 space-y-3">
          <div
            v-for="transfer in clientTransfers"
            :key="transfer.id"
            class="rounded-lg border p-4 flex items-center justify-between"
          >
            <div>
              <div class="text-sm font-medium">{{ transfer.name }}</div>
              <div class="text-xs text-muted-foreground mt-0.5 uppercase tracking-wide">{{ transfer.protocol }}</div>
            </div>
          </div>
          <p v-if="!clientTransfers.length" class="text-sm text-muted-foreground py-4">No client transfers configured.</p>
        </div>
      </template>

      <!-- ═══ Developer API Keys ═══ -->
      <template v-if="activeSection === 'apikeys'">
        <div class="mb-2">
          <h1 class="text-lg font-semibold">Developer API Keys</h1>
          <p class="text-sm text-muted-foreground mt-0.5">Group API keys for programmatic access to the NewSpark API.</p>
        </div>
        <Separator />

        <div class="mt-6 rounded-lg border p-6 flex flex-col items-start gap-4">
          <p class="text-sm text-muted-foreground">
            Group API keys are managed in the classic admin interface.
          </p>
          <a
            :href="`/credentials?vid=${vhostId}#apikeys`"
            target="_blank"
            class="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            <ExternalLink class="h-4 w-4" />
            Manage API Keys
          </a>
        </div>
      </template>
    </main>
  </div>

  <!-- Add Integration dialog -->
  <Dialog v-model:open="showAddIntegration">
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Add Integration Credential</DialogTitle>
        <DialogDescription>Add credentials for an external service.</DialogDescription>
      </DialogHeader>
      <div class="space-y-3 py-2">
        <div class="grid gap-1.5">
          <Label>Service type</Label>
          <Input v-model="newIntegrationType" placeholder="e.g. FRAMEIO, TMS_LIONBRIDGE" class="text-sm" />
        </div>
        <div class="grid gap-1.5">
          <Label>Client ID / Username</Label>
          <Input v-model="newIntegrationClientId" class="text-sm" />
        </div>
        <div class="grid gap-1.5">
          <Label>Secret / Password / Token</Label>
          <Input v-model="newIntegrationSecret" type="password" class="text-sm" autocomplete="off" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" @click="showAddIntegration = false">Cancel</Button>
        <Button :disabled="savingIntegration || !newIntegrationType || !newIntegrationClientId" @click="addIntegrationCredential" class="gap-1.5">
          <Loader2 v-if="savingIntegration" class="h-4 w-4 animate-spin" />
          Add
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
